import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authApi } from '../services/api';
import { UserWithRoles } from '../types/generated';
import { checkAuthenticationWithRetry, buildLoginRedirectUrl } from '../utils/authHelper';

interface FairManagerAuthGuardProps {
  children: React.ReactNode;
}

const FairManagerAuthGuard: React.FC<FairManagerAuthGuardProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        // Check if user has token and is logged in
        const loggedIn = authApi.isLoggedIn();

        if (loggedIn) {
          // Verify token with timeout and retry logic
          const result = await checkAuthenticationWithRetry();

          if (result.success) {
            setIsAuthenticated(true);
            // Issue #922: Check roles directly from API response instead of localStorage
            // to avoid stale data issues. Fair Manager OR Admin can access.
            const typedUserData = result.userData as UserWithRoles;
            const roles = typedUserData.roles || [];
            // Handle both role object format {name: string} and string array format (for tests)
            const roleNames = roles.map(role => typeof role === 'string' ? role : role.name);
            const hasAccess = roleNames.includes('Fair Manager') || roleNames.includes('Admin');
            setIsAuthorized(hasAccess);
          } else {
            // Authentication failed after retries - logout and redirect
            authApi.logout();
            setIsAuthenticated(false);
            setIsAuthorized(false);
          }
        } else {
          setIsAuthenticated(false);
          setIsAuthorized(false);
        }
      } catch (err: unknown) {
        console.error('Authentication check failed:', err);
        setIsAuthenticated(false);
        setIsAuthorized(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthentication();
  }, []);

  // Use useEffect to handle redirect separately to avoid re-render loops
  useEffect(() => {
    if (!isLoading && (!isAuthenticated || !isAuthorized)) {
      const redirectPath = location.pathname + location.search;
      const loginPath = buildLoginRedirectUrl(redirectPath);
      navigate(loginPath, { replace: true });
    }
  }, [isLoading, isAuthenticated, isAuthorized, location.pathname, location.search, navigate]);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying fair manager permissions...</p>
        </div>
      </div>
    );
  }

  // Don't render anything while redirecting
  if (!isAuthenticated || !isAuthorized) {
    return null;
  }

  // User is authenticated and authorized as fair manager or admin, render children
  return <>{children}</>;
};

export default FairManagerAuthGuard;
